import {
  ConditionalCheckFailedException,
  DynamoDBClient,
  GetItemCommand,
  PutItemCommand,
  QueryCommand,
} from '@aws-sdk/client-dynamodb'
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb'
import type { CreateWealthFeedJobRecord, WealthFeedJobRecord } from './schema'
import { wealthFeedJobSchema } from './schema'

export class WealthFeedJobRepository {
  private readonly client = new DynamoDBClient({})
  private readonly tableName = 'wealthfeed_events'

  /**
   * Idempotent write keyed by WealthFeed's own jobId.
   * attribute_not_exists(jobId) ensures a webhook replay never overwrites
   * a completed record (WealthFeed guarantees the same jobId on replays).
   */
  async createJobRecord(job: CreateWealthFeedJobRecord): Promise<void> {
    try {
      await this.client.send(
        new PutItemCommand({
          TableName: this.tableName,
          Item: marshall(job),
          ConditionExpression: 'attribute_not_exists(jobId)',
        })
      )
    } catch (err) {
      if (err instanceof ConditionalCheckFailedException) return
      throw err
    }
  }

  async getByJobId(jobId: number): Promise<WealthFeedJobRecord | null> {
    const result = await this.client.send(
      new GetItemCommand({
        TableName: this.tableName,
        Key: marshall({ jobId }),
      })
    )
    return result.Item ? wealthFeedJobSchema.parse(unmarshall(result.Item)) : null
  }

  async getBySalesforceLeadId(salesforceLeadId: string): Promise<WealthFeedJobRecord | null> {
    const result = await this.client.send(
      new QueryCommand({
        TableName: this.tableName,
        IndexName: 'salesforceLeadId-index',
        KeyConditionExpression: 'salesforceLeadId = :sfLeadId',
        ExpressionAttributeValues: marshall({ ':sfLeadId': salesforceLeadId }),
        Limit: 1,
      })
    )
    const item = result.Items?.[0]
    return item ? wealthFeedJobSchema.parse(unmarshall(item)) : null
  }
}
